"use client"
import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useAudio } from "@/app/contexts/AudioContext"
import AudioIcon from "@/app/common/blocks/AudioIcon"
import { isModifiedClick } from "@/app/common/isModifiedClick"
import { SupplementsList } from "./SupplementsList"

// The scenario PDF (the `npm run pdf:scenario` output) is served same-origin
// from /public, so a temporary <a download> saves it directly — no blob fetch
// needed. The visible links keep a plain href without the download attribute
// so modified clicks (cmd/ctrl/middle) still open the inline view in a tab.
const PDF_DOWNLOAD_URL = "/AI-2040.pdf"
const PDF_FILENAME = "AI-2040.pdf"

const downloadPdf = () => {
  const anchor = document.createElement("a")
  anchor.href = PDF_DOWNLOAD_URL
  anchor.download = PDF_FILENAME
  anchor.click()
}

// Toggles the scrub-timeline panel: opening reveals the UI AND starts
// playback immediately (no separate play click), closing pauses so audio
// never plays with the UI hidden. The narration is of the main Plan A page,
// so the audio UI only shows there; navigating to any other page pauses
// playback rather than leaving it running with no controls on screen.
const NarrationButton = ({ isHomePage }: { isHomePage: boolean }) => {
  const { isPlaying, isVisible, togglePlayPause, setIsVisible } = useAudio()
  useEffect(() => {
    if (!isHomePage && isPlaying) togglePlayPause()
  }, [isHomePage, isPlaying, togglePlayPause])
  if (!isHomePage) return null
  const togglePanel = () => {
    if (isVisible) {
      if (isPlaying) togglePlayPause()
      setIsVisible(false)
    } else {
      setIsVisible(true)
      if (!isPlaying) togglePlayPause()
    }
  }
  // The icon only toggles `isVisible`; the actual player renders as the HUD
  // bottom bar (see GraphAudioControls). No dropdown player at the top of the
  // page. Solid black in both states (no opacity dimming, per the design
  // style guide); the open state reads through the heavier stroke instead.
  return (
    <div className="relative flex items-center gap-2.5">
      <button
        type="button"
        onClick={togglePanel}
        aria-label={isVisible ? "Close audio player" : "Listen to narration"}
        aria-expanded={isVisible}
        className="flex h-7 w-7 -m-[5px] items-center justify-center rounded-full border-0 p-0 cursor-pointer bg-transparent"
      >
        <AudioIcon strokeWidth={isVisible ? 2.5 : 1.5} />
      </button>
    </div>
  )
}

export const GoodEndingNav = () => {
  const [isOpen, setIsOpen] = useState(false)
  const isHomePage = usePathname() === "/"

  const handlePdfClick = (event: React.MouseEvent) => {
    // cmd/ctrl/shift/middle-click: let the browser open the URL natively
    // (inline view in a new tab/window).
    if (isModifiedClick(event)) return
    event.preventDefault()
    downloadPdf()
  }

  // Render both nav variants and gate them with the `md` breakpoint (930px,
  // matching the 2-column layout switch) so the SSR output is already correct
  // for the viewport — a JS media query would flash the desktop nav on mobile
  // until hydration.
  return (
    <>
      <nav className="md:hidden mt-2 relative z-[70] mr-4 sm:mr-0 flex items-center gap-4" onClick={(event) => event.stopPropagation()}>
        <NarrationButton isHomePage={isHomePage} />
        <button
          type="button"
          className="text-3xl leading-none bg-transparent border-0 p-0 cursor-pointer"
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Menu"
          aria-expanded={isOpen}
        >
          ≡
        </button>
        {isOpen && (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-0 bg-black/25 border-0 cursor-default"
            onClick={() => setIsOpen(false)}
          />
        )}
        {isOpen && (
          <div className="fixed left-2.5 right-2.5 top-16 max-h-[calc(100dvh-4.625rem)] z-[1] flex flex-col items-stretch font-sans text-sm leading-tight whitespace-normal bg-[#fffff8] pl-3 pr-2.5 rounded-sm border border-black text-black overflow-y-auto overflow-x-hidden">
            <a className="border-b border-black py-3 no-underline font-semibold hover:no-underline flex items-center" href="https://ai-futures.org/" target="_blank">
              <Image src="/ai-futures-logo.svg" alt="AI Futures Project" width={16} height={16} className="mr-2" /> AI Futures Homepage
            </a>
            <a className="border-b font-semibold border-black py-3 no-underline hover:underline hover:text-[#8b0000]" href={PDF_DOWNLOAD_URL} onClick={handlePdfClick}>PDF</a>
            <Link className="border-b font-semibold border-black py-3 no-underline hover:underline hover:text-[#8b0000]" href="/about">About</Link>
            <Link className="border-b font-semibold border-black py-3 no-underline hover:underline hover:text-[#8b0000]" href="/summary">Summary</Link>
            {/* <a className="border-b font-semibold border-black py-3 no-underline hover:underline hover:text-[#8b0000]" href="/changelog" target="_blank">Changelog</a> */}
            <div className="pt-3 pb-2">
               <div className="text-sm pb-1 font-semibold">Supplements</div>
               <div className="flex flex-col">
                <SupplementsList className="py-2 no-underline hover:font-semibold hover:text-[#8b0000]" /></div>
            </div>
          </div>
        )}
      </nav>
      <nav className="hidden md:flex relative z-[101] gap-12 text-xl mt-6 items-center">
        <NarrationButton isHomePage={isHomePage} />
      <a
        className="no-underline hover:no-underline text-nowrap"
        href={PDF_DOWNLOAD_URL}
        onClick={handlePdfClick}
      >
        PDF
      </a>
      <div className="group relative" onClick={(event) => event.stopPropagation()}>
        <Link href="/supplements" className="cursor-pointer no-underline hover:no-underline text-inherit">Supplements</Link>
        <div className="absolute right-0 font-sans text-sm top-full hidden group-hover:flex flex-col whitespace-nowrap bg-[#fffff8] px-3 pb-1 rounded-sm border border-black text-black">
          <SupplementsList className="py-1 no-underline hover:font-semibold hover:text-[#8b0000]" showPreview />
          {/* <a className="mt-3 border-t border-black no-underline hover:font-semibold hover:text-[#8b0000] py-2.5" href="/changelog" target="_blank">Changelog</a> */}
        </div>
      </div>
      <div className="group relative" onClick={(event) => event.stopPropagation()}>
      <Link className="no-underline hover:no-underline text-nowrap" href="/about">About</Link>
      </div>
      <div className="group relative" onClick={(event) => event.stopPropagation()}>
      <Link className="no-underline hover:no-underline text-nowrap" href="/summary">Summary</Link>
      </div>
      <a className="no-underline hover:no-underline" href="https://ai-futures.org/" target="_blank" style={{ height: 16 }}>
        <Image src="/ai-futures-logo.svg" alt="AI Futures Project" width={16} height={16} />
      </a>
      </nav>
    </>
  )
}
